import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import { Service } from '../services/Service';
import { AcceptInvitationRequest } from '../models/User';
import { useAuthStore } from '@shared/stores/authStore';
import { AuthService, AuthApiAdapter } from '@modules/Auth';

const AUTH_QUERY_KEYS = {
  user: ['auth', 'user'],
  currentUser: ['auth', 'current-user'],
} as const;

/**
 * Hook para aceptar una invitación de usuario
 * Maneja la aceptación de la invitación, autenticación automática y redirección
 */
export const useAcceptInvitation = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('usersModule');
  const queryClient = useQueryClient();
  const { setUser, setToken, setAuthenticated, setTeam, setRole } = useAuthStore();

  const httpClient = createHttpClient(apiClientWH);
  const usersAdapter = new UsersApiAdapter(httpClient);
  const usersService = new Service(usersAdapter);

  return useMutation({
    mutationFn: (request: AcceptInvitationRequest) => usersService.acceptInvitation(request),
    onSuccess: async (response) => {
      try {
        // 1. Guardar token JWT recibido
        setToken(response.token);
        setAuthenticated(true);

        // 2. Obtener datos del usuario actual con el token guardado
        const authHttpClient = createHttpClient(apiClientWH);
        const authAdapter = new AuthApiAdapter(authHttpClient);
        const authService = new AuthService(authAdapter);
        
        const userData = await authService.getCurrentUser();

        // 3. Guardar datos del usuario en el store
        setUser(userData.user);
        setTeam(userData.team ?? null);
        setRole(userData.role ?? null);
        queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, userData);

        // 4. Invalidar cache de usuario
        queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.user });

        // 5. Mostrar mensaje de éxito
        toast({
          title: t('acceptInvitation.success.title', 'Invitación aceptada'),
          description: t('acceptInvitation.success.description', 'Bienvenido al equipo'),
          variant: 'default',
        });

        // 6. Redirigir a home
        navigate('/home', { replace: true });
      } catch (userError) {
        console.warn('Error obteniendo usuario después de aceptar invitación:', userError);
        // Aún redirigimos, el usuario se puede cargar después
        navigate('/home', { replace: true });
      }
    },
    onError: (error: any) => {
      console.error('Error al aceptar invitación:', error);
      
      // Si el error indica que la invitación fue aceptada pero sin token (usuario existente)
      if (error.message?.includes('INVITATION_ACCEPTED_NO_TOKEN')) {
        toast({
          title: t('acceptInvitation.success.title', 'Invitación aceptada'),
          description: t(
            'acceptInvitation.success.userExists',
            'La invitación fue aceptada. Por favor inicia sesión con tu cuenta.'
          ),
          variant: 'default',
        });
        // Redirigir al login
        navigate('/login', { replace: true });
        return;
      }
      
      // Mostrar mensaje de error específico
      let errorMessage = t('acceptInvitation.errors.unexpected', 'Error inesperado al aceptar la invitación');
      
      if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('acceptInvitation.errors.title', 'Error al aceptar invitación'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

